import { useCallback, useRef } from 'react';

import { safeLocalStorage } from 'Utilities/storages';

import { calculateServiceFee, getServiceFeeTiers } from 'Services/privateApis';

import type { ServiceFeeCalculateResponse, ServiceFeeTiersResponse } from '../../../../server/views/serviceFees/serviceFees.types';

type ServiceFeeDataResult = {
  fee?: ServiceFeeCalculateResponse;
  tiers?: ServiceFeeTiersResponse;
};

type UseServiceFeeDataOptions = {
  onError?: () => void;
  onSuccess?: (result: ServiceFeeDataResult) => void;
};

const FEE_NOTE_CREATED_KEY = 'fee-note-created';

const hasNoteBeenCreated = (dpEnquiryId: number): boolean => {
  const ids: number[] = safeLocalStorage.getItem(FEE_NOTE_CREATED_KEY) ?? [];
  return Array.isArray(ids) && ids.includes(dpEnquiryId);
};

const markNoteCreated = (dpEnquiryId: number): void => {
  const ids: number[] = safeLocalStorage.getItem(FEE_NOTE_CREATED_KEY) ?? [];
  const list = Array.isArray(ids) ? ids : [];
  if (!list.includes(dpEnquiryId)) {
    safeLocalStorage.setItem(FEE_NOTE_CREATED_KEY, [...list, dpEnquiryId]);
  }
};

const useServiceFeeData = ({ onError, onSuccess }: UseServiceFeeDataOptions = {}) => {
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const fetchServiceFeeData = useCallback(
    (price?: number, dpEnquiryId?: number) => {
      if (!price || !dpEnquiryId) {
        onErrorRef.current?.();
        return;
      }

      const shouldIncludeEnquiryId = !hasNoteBeenCreated(dpEnquiryId);
      const payload = { price, ...(shouldIncludeEnquiryId && { dpEnquiryId }) };

      Promise.allSettled([
        calculateServiceFee(payload),
        getServiceFeeTiers(),
      ]).then(([feeResult, tiersResult]) => {
        const fee = feeResult.status === 'fulfilled' ? feeResult.value as ServiceFeeCalculateResponse : undefined;
        const tiers = tiersResult.status === 'fulfilled' ? tiersResult.value as ServiceFeeTiersResponse : undefined;

        if (fee || tiers) {
          if (fee && shouldIncludeEnquiryId) {
            markNoteCreated(dpEnquiryId);
          }
          onSuccessRef.current?.({ fee, tiers });
        } else {
          onErrorRef.current?.();
        }
      });
    },
    [],
  );

  return { fetchServiceFeeData };
};

export {
  useServiceFeeData,
  type ServiceFeeCalculateResponse,
  type ServiceFeeTiersResponse,
};
